import { use_slice_ui } from '@/hooks/store/use_slices';
import { FN_IMAGE__get_best_first_tab_for_image_edit } from '@/utils/_methods/FN_IMAGE__get_best_first_tab_for_image_edit';
import { ENUM__field_kind } from '@shared/dto/generation/graph_invoke/_enums/ENUM__field_kind';
import { ENUM__image_asset_category } from '@shared/dto/generation/graph_invoke/_fields/ENUM__image_asset_category';
import { FIELD__image_asset } from '@shared/dto/generation/graph_invoke/_fields/image/FIELD__image_asset';
import { useMemo } from 'react';
import { ENUM__page_generation_layout } from '../../_enums/ENUM__page_generation_layout';
import { G_WRAPPER_IMAGE__preview_on_layout_output } from '../../_watchers/G_WRAPPER_IMAGE__preview_on_layout_output';
import { G_GRAPH__FIELD__image_asset } from '../../layouts/nodes/node/fields/image/image_asset/G_GRAPH__FIELD__image_asset';
import { G_HOOK__output__field_hovered_set } from '../_base/_hooks/G_HOOK__output__field_hovered_set';
import { G_UI_FIELD__header } from '../_base/G_UI_FIELD__header';
import { FN_FIELD_IMAGE__edit } from './_methods/FN_FIELD_IMAGE__edit';
import { G_UI_FIELD__image_asset_empty_color_map } from './empty/G_UI_FIELD__image_asset_empty_color_map';
import { G_UI_FIELD__image_asset_empty_control_depth } from './empty/G_UI_FIELD__image_asset_empty_control_depth';
import { G_UI_FIELD__image_asset_empty_control_line_guidance_canny } from './empty/G_UI_FIELD__image_asset_empty_control_line_guidance_canny';
import { G_UI_FIELD__image_asset_empty_control_line_soft_edge } from './empty/G_UI_FIELD__image_asset_empty_control_line_soft_edge';
import { G_UI_FIELD__image_asset_empty_image } from './empty/G_UI_FIELD__image_asset_empty_image';
import { G_UI_FIELD__image_asset_text } from './G_UI_FIELD__image_asset_text';

interface PROPS {
  field: FIELD__image_asset;
  class_name?: string;
}

export function G_UI_FIELD__image_asset({ field, class_name = '' }: PROPS) {
  /*
   * STORE
   */

  const { g_image_edit__field, g_general__layout, g_image_edit__tab } = use_slice_ui(['g_image_edit__field', 'g_general__layout', 'g_image_edit__tab']);

  const has_content = useMemo(() => is_has_content(), [field]);

  /*
   * STATE
   */
  const { onMouseOver, onMouseLeave } = G_HOOK__output__field_hovered_set({ field });
  /*
   * EVENTS
   */
  function onClick() {
    try_select_on_layout_image_replace();
  }

  /*
   * METHODS
   */
  function try_select_on_layout_image_replace() {
    if (g_image_edit__field.meta.field_id == field.meta.field_id) return; //already selected
    if (g_general__layout != ENUM__page_generation_layout.ASSETS) return; //not in image select

    let best_tab = FN_IMAGE__get_best_first_tab_for_image_edit(field);
    FN_FIELD_IMAGE__edit(field, g_general__layout, best_tab);
  }

  function is_has_content() {
    if (!field.get_has_content()) return false;
    if (field.is_category_mask) return true; //black image for mask is valid image with content
    if (field.asset.is_single_color_black && field.meta.field_kind == ENUM__field_kind.INPUT) return false;

    return true;
  }

  /*
   * RENDERS
   */

  function render_content_input() {
    if (!field.meta.kind__is_input) return null; //not output
    if (field.meta.data__is_linked) return <G_UI_FIELD__image_asset_text text={'Field is linked'} {...{ field }} />;
    if (field.meta.ui__is_readonly) return <G_UI_FIELD__image_asset_text text={'Field is read only'} {...{ field }} />;
    if (!has_content) return render_input_empty();
    if (!field.image__is_initialized) return <G_UI_FIELD__image_asset_text text={'Loading'} {...{ field }} />;

    return render_field_with_content();
  }

  function render_input_empty() {
    let out_map = new Map<ENUM__image_asset_category, React.ReactNode>([
      [ENUM__image_asset_category.IMAGE, <G_UI_FIELD__image_asset_empty_image field={field} />],
      [ENUM__image_asset_category.COLOR_MAP, <G_UI_FIELD__image_asset_empty_color_map field={field} />],
      [ENUM__image_asset_category.LINE__CANNY, <G_UI_FIELD__image_asset_empty_control_line_guidance_canny field={field} />],
      [ENUM__image_asset_category.LINE__LINEART, <></>],
      [ENUM__image_asset_category.LINE__SOFT_EDGE, <G_UI_FIELD__image_asset_empty_control_line_soft_edge field={field} />],
      [ENUM__image_asset_category.DEPTH, <G_UI_FIELD__image_asset_empty_control_depth field={field} />],
      [ENUM__image_asset_category.MASK, <></>], //todo: mb need provide button create mask manually and open tab to edit mask
      [ENUM__image_asset_category.ANY, <></>], //todo: mb need remove this and handle in other way
    ]);

    let out = out_map.get_or_assert(field.category);

    return out;
  }

  function render_field_with_content() {
    return (
      <G_WRAPPER_IMAGE__preview_on_layout_output image_asset={field.asset}>
        <G_GRAPH__FIELD__image_asset {...{ field }} />
      </G_WRAPPER_IMAGE__preview_on_layout_output>
    );
  }

  /*
   *
   */

  function render_content_output() {
    if (field.meta.field_kind != ENUM__field_kind.OUTPUT) return null; //not output
    if (!has_content) return <G_UI_FIELD__image_asset_text text={'Image is not generated yet'} {...{ field }} />;
    if (!field.image__is_initialized) return <G_UI_FIELD__image_asset_text text={'Loading'} {...{ field }} />;

    return render_field_with_content();
  }

  /*
   * HTML
   */

  return (
    <div className={`w-full ${class_name}`} {...{ onClick, onMouseOver, onMouseLeave }}>
      <G_UI_FIELD__header {...{ field }} />
      {render_content_input()}
      {render_content_output()}
    </div>
  );
}
